import { useState, useRef, useEffect, useCallback, useId, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Popover } from "./popover";

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  /** Names the combobox; the selected pills are its value, not its name. */
  "aria-label"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

/**
 * A checkbox list over `{label, value}` options behind a pill trigger. The
 * list is the app's `Popover`, portaled to the body, so a scrolling box or a
 * drawer around the control never clips it. Focus stays on the trigger, which
 * owns the keys: arrows move the highlight, Enter or Space toggles it; the
 * popover closes the list on Escape and on an outside press.
 */
export function MultiSelect({
  values,
  onChange,
  options,
  placeholder = "",
  disabled = false,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid = false,
}: MultiSelectProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [focusIndex, setFocusIndex] = useState(-1);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const id = useId();

  const close = useCallback(() => {
    setOpen(false);
    setFocusIndex(-1);
  }, []);

  useEffect(() => {
    if (open && focusIndex >= 0 && listRef.current) {
      const item = listRef.current.children[focusIndex] as HTMLElement | undefined;
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [focusIndex, open]);

  const toggle = useCallback(
    (val: string) => {
      if (values.includes(val)) {
        onChange(values.filter((v) => v !== val));
      } else {
        onChange([...values, val]);
      }
    },
    [values, onChange],
  );

  const removePill = useCallback(
    (val: string, e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(values.filter((v) => v !== val));
    },
    [values, onChange],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
        setFocusIndex(0);
      }
      return;
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusIndex((i) => Math.min(i + 1, options.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (focusIndex >= 0 && focusIndex < options.length) {
          const opt = options[focusIndex];
          if (opt) toggle(opt.value);
        }
        break;
    }
  };

  const selectedLabels = options.filter((o) => values.includes(o.value));

  return (
    <>
      <div
        ref={triggerRef}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={`${id}-list`}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid || undefined}
        tabIndex={disabled ? -1 : 0}
        className={`flex min-h-[38px] w-full flex-wrap items-center gap-1 rounded-md border px-2 py-1.5 text-sm ${
          disabled
            ? "cursor-not-allowed bg-gray-100 dark:bg-gray-800"
            : "cursor-pointer bg-white dark:bg-gray-700"
        } border-gray-300 dark:border-gray-600 dark:text-white`}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
      >
        {selectedLabels.length === 0 && <span className="text-muted">{placeholder}</span>}
        {selectedLabels.map((opt) => (
          <span
            key={opt.value}
            className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-600 dark:text-gray-200"
          >
            {opt.label}
            <button
              type="button"
              aria-label={t("components.multiSelect.remove", { label: opt.label })}
              className="hover:text-danger"
              onClick={(e) => removePill(opt.value, e)}
            >
              x
            </button>
          </span>
        ))}
      </div>
      <Popover
        open={open}
        anchorRef={triggerRef}
        onClose={close}
        role="listbox"
        id={`${id}-list`}
        width="anchor"
        returnFocus={false}
        initialFocusRef={triggerRef}
      >
        <div ref={listRef}>
          {options.map((opt, idx) => {
            const checked = values.includes(opt.value);
            return (
              <div
                key={opt.value}
                role="option"
                aria-selected={checked}
                className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm ${
                  idx === focusIndex
                    ? "bg-gray-100 dark:bg-gray-600"
                    : "hover:bg-gray-50 dark:hover:bg-gray-600"
                } text-on-surface`}
                onClick={() => toggle(opt.value)}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  readOnly
                  tabIndex={-1}
                  className="pointer-events-none"
                />
                {opt.label}
              </div>
            );
          })}
        </div>
      </Popover>
    </>
  );
}
